import Phaser from 'phaser';
import { GAME_CONSTANTS } from '../config/balance';
import { CubeRenderer } from './CubeRenderer';
import { TrailRenderer } from './TrailRenderer';
import { PlayerEffect } from './PlayerEffect';
import { ItemType } from '../config/items';
import { getTextResolution } from '../utils/display';

export class Enemy extends Phaser.GameObjects.Container {
  public id: string;
  // 항상 월드(픽셀) 좌표로 보관한다 - 렌더링과 충돌 판정이 동일 좌표계를 쓰도록
  public trail: { x: number; y: number }[] = [];
  private lastTrailRef?: { x: number; y: number }[];

  private cubeRenderer: CubeRenderer;
  private trailRenderer: TrailRenderer;
  private nicknameText: Phaser.GameObjects.Text;
  private effectTimerText: Phaser.GameObjects.Text;
  private targetPosition: { x: number; y: number };
  private direction: number = 0;
  private size: number;
  private color: number;
  private nickname: string;
  private lastStateAt: number = Date.now();
  // 원격 동기화된 아이템 효과
  private effect = new PlayerEffect();

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    color: number,
    id: string,
    nickname: string
  ) {
    super(scene, x, y);
    this.id = id;
    this.color = color;
    this.nickname = nickname;
    this.size = GAME_CONSTANTS.map.tileSize;
    this.targetPosition = { x, y };

    scene.add.existing(this);
    this.setDepth(10);

    // 큐브 렌더러
    this.cubeRenderer = new CubeRenderer(scene, this.size, color);
    this.cubeRenderer.setDepth(10);

    // 트레일 렌더러
    this.trailRenderer = new TrailRenderer(scene, this.size, color);

    // 닉네임
    this.nicknameText = scene.add.text(0, -this.size - 10, nickname, {
      fontSize: '14px',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 3,
      fontFamily: 'Nunito',
      resolution: getTextResolution(),
    }).setOrigin(0.5);

    // 효과 남은시간 카운터 (닉네임 위)
    this.effectTimerText = scene.add.text(0, 0, '', {
      fontSize: '14px',
      color: '#FFE14E',
      stroke: '#000000',
      strokeThickness: 3,
      fontFamily: 'Nunito',
      fontStyle: 'bold',
      resolution: getTextResolution(),
    }).setOrigin(0.5).setDepth(16).setVisible(false);
  }

  update(_delta: number): void {
    // 원격 효과 만료 + 시각 반영
    this.effect.update(Date.now());
    this.applyEffectVisuals();

    // 선형 보간으로 부드러운 이동
    const lerpFactor = Math.min(
      1,
      Math.max(0.18, _delta / GAME_CONSTANTS.network.positionInterpolation)
    );

    const prevX = this.x;
    const prevY = this.y;
    const distanceToTarget = Phaser.Math.Distance.Between(
      this.x,
      this.y,
      this.targetPosition.x,
      this.targetPosition.y
    );

    if (distanceToTarget > GAME_CONSTANTS.map.tileSize * 12) {
      this.x = this.targetPosition.x;
      this.y = this.targetPosition.y;
    } else {
      this.x = Phaser.Math.Linear(this.x, this.targetPosition.x, lerpFactor);
      this.y = Phaser.Math.Linear(this.y, this.targetPosition.y, lerpFactor);
    }

    // 이동 방향 계산
    if (Math.abs(this.x - prevX) > 0.1 || Math.abs(this.y - prevY) > 0.1) {
      this.direction = Math.atan2(this.y - prevY, this.x - prevX);
      this.cubeRenderer.setDirection(this.direction);
    }

    // 목표 지점에 거의 도달하면 고정
    if (Phaser.Math.Distance.Between(this.x, this.y, this.targetPosition.x, this.targetPosition.y) < 1) {
      this.x = this.targetPosition.x;
      this.y = this.targetPosition.y;
    }

    // 큐브 렌더링
    this.cubeRenderer.render(this.x, this.y);

    // 닉네임 위치 업데이트 (거대화 시 큐브가 커지므로 높이 반영)
    const cubeHeight = this.cubeRenderer.getTotalHeight();
    this.nicknameText.setPosition(this.x, this.y - cubeHeight - 14);

    // 효과 남은시간 카운터
    const remMs = this.effect.remainingMs(Date.now());
    if (remMs > 0) {
      this.effectTimerText.setText(`⏱ ${Math.ceil(remMs / 1000)}`);
      this.effectTimerText.setPosition(this.x, this.y - cubeHeight - 36);
      this.effectTimerText.setVisible(true);
    } else {
      this.effectTimerText.setVisible(false);
    }
  }

  /** 원격에서 받은 효과를 적용 (handlePlayerMove/handleBotMove → syncRemotePlayers) */
  applyRemoteEffect(effect: { type: ItemType; remainingMs: number } | null | undefined): void {
    this.effect.setFromRemote(effect?.type ?? null, effect?.remainingMs ?? 0, Date.now());
  }

  /** 효과에 따른 크기/꼬리굵기/무지개 시각 반영 (변화 없으면 저렴) */
  private applyEffectVisuals(): void {
    this.cubeRenderer.setSizeMultiplier(this.effect.sizeMultiplier());
    this.cubeRenderer.setRainbow(this.effect.isRainbow());
    this.trailRenderer.setWidth(this.cubeRenderer.getBaseWidth());
    this.trailRenderer.setRainbow(this.effect.isRainbow());
    this.trailRenderer.tickRainbow();
  }

  /** 충돌 판정용: 이 원격 캐릭터가 무적인가 */
  isInvincibleNow(): boolean {
    return this.effect.isInvincible();
  }

  /** 충돌 판정용: 이 원격 캐릭터의 크기 배율 (거대화 시 판정 반경 확대) */
  getSizeMultiplier(): number {
    return this.effect.sizeMultiplier();
  }

  updateState(
    x: number,
    y: number,
    // undefined = trail 미포함 패킷이므로 기존 trail 유지, [] = 명시적 trail 제거
    trail?: { x: number; y: number }[],
    direction?: number,
    trailSpace: 'grid' | 'world' = 'grid'
  ): void {
    this.targetPosition.x = x;
    this.targetPosition.y = y;
    this.lastStateAt = Date.now();

    // 같은 배열 참조면 내용이 안 바뀐 것이므로 변환/재렌더링 생략
    // (스토어 구독으로 매 업데이트마다 호출되기 때문에 중요)
    if (trail && trail !== this.lastTrailRef) {
      this.lastTrailRef = trail;

      const tileSize = GAME_CONSTANTS.map.tileSize;
      // 구버전 클라이언트의 그리드(타일) trail은 계단 모양이므로
      // Chaikin 코너 컷팅으로 부드럽게 보정한다
      const worldTrail = trailSpace === 'world'
        ? trail.slice()
        : Enemy.smoothPath(
            trail.map((point) => ({
              x: point.x * tileSize + tileSize / 2,
              y: point.y * tileSize + tileSize / 2,
            })),
            2
          );

      this.trail = worldTrail;
      this.renderTrail(worldTrail);
    }

    if (direction !== undefined) {
      this.direction = direction;
      this.cubeRenderer.setDirection(direction);
    }
  }

  getDirection(): number {
    return this.direction;
  }

  getNickname(): string {
    return this.nickname;
  }

  getColor(): number {
    return this.color;
  }

  getLastStateAt(): number {
    return this.lastStateAt;
  }

  isStale(now: number, timeoutMs: number): boolean {
    return now - this.lastStateAt > timeoutMs;
  }

  clearTrail(): void {
    this.trail = [];
    this.lastTrailRef = undefined;
    this.trailRenderer.clear();
  }

  /**
   * Chaikin 코너 컷팅 - 계단 모양 폴리라인을 부드러운 곡선으로
   * (각 선분을 1/4, 3/4 지점으로 잘라 모서리를 둥글린다)
   */
  private static smoothPath(
    points: { x: number; y: number }[],
    iterations: number
  ): { x: number; y: number }[] {
    let current = points;

    for (let iter = 0; iter < iterations; iter++) {
      if (current.length < 3) return current;

      const smoothed: { x: number; y: number }[] = [current[0]];
      for (let i = 0; i < current.length - 1; i++) {
        const a = current[i];
        const b = current[i + 1];
        smoothed.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
        smoothed.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
      }
      smoothed.push(current[current.length - 1]);
      current = smoothed;
    }

    return current;
  }

  private renderTrail(worldTrail: { x: number; y: number }[]): void {
    if (worldTrail.length < 2) {
      this.trailRenderer.clear();
      return;
    }

    this.trailRenderer.setPoints(worldTrail);
  }

  destroy(fromScene?: boolean): void {
    this.cubeRenderer.destroy();
    this.trailRenderer.destroy();
    this.nicknameText.destroy();
    this.effectTimerText.destroy();
    super.destroy(fromScene);
  }
}
